import { useEffect, useState } from "react";
import { Phone, MessageCircle, ShieldCheck, Star, MapPin, Clock4 } from "lucide-react";

const PHONE_DISPLAY = "(865) 621-0995";
const PHONE_TEL = "+18656210995";
const TEXT_BODY = encodeURIComponent(
  "Hi Acorn Tree Service² — saw your ad. I've got a tree that needs handled. Here's a photo:",
);

export default function Ad() {
  const [now, setNow] = useState(new Date());

  // Live "AVAILABLE NOW" clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  // Open hours: Mon-Sat 7am-7pm, Sun emergency only
  const hour = now.getHours();
  const day = now.getDay(); // 0 = Sun
  const isOpen = day !== 0 && hour >= 7 && hour < 19;
  const statusText = isOpen ? "AVAILABLE NOW — CALL US" : "AFTER HOURS — TEXT FOR EMERGENCY";

  return (
    <div className="min-h-screen bg-bone font-body text-ink overflow-x-hidden">
      {/* LIVE BADGE */}
      <div className="bg-safety text-bone py-2 px-4 text-center border-b-2 border-forest">
        <div className="flex items-center justify-center gap-2 text-xs uppercase tracking-[0.25em] font-bold">
          <span className="w-2 h-2 bg-bone rounded-full animate-pulse" />
          {statusText}
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 sm:py-10">
        {/* AD IMAGE */}
        <div className="border-4 border-forest hard-shadow-lg overflow-hidden mb-6">
          <img
            src="/acorn-ad-card.jpg"
            alt="Acorn Tree Service² — (865) 621-0995"
            className="block w-full"
            data-testid="ad-image"
          />
        </div>

        {/* CALL BUTTON — MASSIVE */}
        <a
          href={`tel:${PHONE_TEL}`}
          data-testid="ad-call-btn"
          className="block bg-safety text-bone border-4 border-forest hard-shadow-lg p-6 mb-4 hover:translate-y-[-2px] active:translate-y-0 transition-transform"
        >
          <div className="flex items-center gap-4 justify-center">
            <div className="w-14 h-14 bg-bone border-4 border-bone flex items-center justify-center rounded-full">
              <Phone className="w-7 h-7 text-safety" strokeWidth={3} fill="currentColor" />
            </div>
            <div className="text-left">
              <div className="text-xs uppercase tracking-[0.25em] font-bold opacity-90">Tap to call now</div>
              <div className="font-display text-3xl sm:text-4xl tracking-tight leading-none mt-1">
                {PHONE_DISPLAY}
              </div>
            </div>
          </div>
        </a>

        {/* TEXT BUTTON */}
        <a
          href={`sms:${PHONE_TEL}?&body=${TEXT_BODY}`}
          data-testid="ad-text-btn"
          className="block bg-forest text-bone border-4 border-forest hard-shadow-lg p-5 mb-6 hover:translate-y-[-2px] active:translate-y-0 transition-transform"
        >
          <div className="flex items-center gap-4 justify-center">
            <div className="w-12 h-12 bg-safety border-2 border-bone flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-bone" strokeWidth={2.5} />
            </div>
            <div className="text-left">
              <div className="text-xs uppercase tracking-[0.25em] font-bold text-safety">Faster option</div>
              <div className="font-display text-2xl tracking-tight">TEXT US A PHOTO</div>
              <div className="text-xs text-bone/70 mt-0.5">Free quote in minutes</div>
            </div>
          </div>
        </a>

        {/* TRUST BADGES */}
        <div className="grid grid-cols-2 gap-2 mb-6">
          <div className="border-2 border-forest bg-white p-3 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-safety flex-shrink-0" strokeWidth={2.5} />
            <div className="text-xs uppercase tracking-widest font-bold">Fully Insured</div>
          </div>
          <div className="border-2 border-forest bg-white p-3 flex items-center gap-2">
            <Star className="w-5 h-5 text-safety flex-shrink-0" fill="currentColor" />
            <div className="text-xs uppercase tracking-widest font-bold">Free Estimates</div>
          </div>
          <div className="border-2 border-forest bg-white p-3 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-safety flex-shrink-0" strokeWidth={2.5} />
            <div className="text-xs uppercase tracking-widest font-bold">Kingston + 100mi</div>
          </div>
          <div className="border-2 border-forest bg-white p-3 flex items-center gap-2">
            <Clock4 className="w-5 h-5 text-safety flex-shrink-0" strokeWidth={2.5} />
            <div className="text-xs uppercase tracking-widest font-bold">24/7 Storm Line</div>
          </div>
        </div>

        {/* WHAT WE DO QUICK LIST */}
        <div className="border-2 border-forest bg-forest text-bone p-5 mb-6">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-safety mb-3">What we handle</div>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <span className="text-safety font-bold flex-shrink-0">✓</span>
              <span><strong>Dangerous Tree Removal</strong> — leaning over your house? On power lines? We're rolling.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-safety font-bold flex-shrink-0">✓</span>
              <span><strong>Tree Trimming & Removal</strong> — clean shape, safer yard, more light.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-safety font-bold flex-shrink-0">✓</span>
              <span><strong>Dead Tree Removal</strong> — get it down before it falls on something expensive.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-safety font-bold flex-shrink-0">✓</span>
              <span><strong>Lot Clearing & Storm Cleanup</strong> — climb it, cut it, haul it.</span>
            </li>
          </ul>
        </div>

        {/* SECOND CALL — REINFORCE */}
        <a
          href={`tel:${PHONE_TEL}`}
          data-testid="ad-call-btn-2"
          className="block bg-safety text-bone border-4 border-forest hard-shadow p-5 text-center mb-4"
        >
          <div className="font-display text-2xl tracking-tight">CALL {PHONE_DISPLAY}</div>
          <div className="text-xs uppercase tracking-widest mt-1 opacity-90">Free Estimate · No Pressure</div>
        </a>

        {/* DEEP LINK to full site */}
        <a
          href="/"
          className="block text-center text-xs uppercase tracking-[0.3em] text-forest/60 hover:text-safety font-bold py-3"
        >
          → See more on the website
        </a>
      </div>

      {/* MOBILE STICKY CALL BAR — always visible */}
      <div className="fixed bottom-0 left-0 right-0 bg-safety border-t-4 border-forest p-3 z-50 sm:hidden">
        <a
          href={`tel:${PHONE_TEL}`}
          className="flex items-center justify-center gap-3 text-bone font-display text-xl tracking-tight"
          data-testid="ad-sticky-call"
        >
          <Phone className="w-6 h-6" strokeWidth={3} fill="currentColor" />
          TAP TO CALL {PHONE_DISPLAY}
        </a>
      </div>
      <div className="h-16 sm:hidden" />
    </div>
  );
}
